import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { stores } from 'mobx-store';
import { TournamentDTO } from '../../DTO';
import TournamentForm from './TournamentForm';

const TournamentsScreen: React.FC = observer(() => {
  const { liveScoringStore } = stores;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<TournamentDTO | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState<TournamentDTO | null>(null);

  useEffect(() => {
    liveScoringStore.getTournaments();
    liveScoringStore.getSports(); // For form select input
    liveScoringStore.getGenders(); // For form select input
  }, [liveScoringStore]);

  const handleOpenAdd = () => {
    setSelectedTournament(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (tournament: TournamentDTO) => {
    setSelectedTournament(tournament);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    liveScoringStore.getTournaments(); // Refresh list
  };

  const handleOpenDeleteDialog = (tournament: TournamentDTO) => {
    setTournamentToDelete(tournament);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setTournamentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (tournamentToDelete && tournamentToDelete.id) {
      await liveScoringStore.deleteTournament(tournamentToDelete.id);
      handleCloseDeleteDialog();
    }
  };

  return (
    <Container>
      <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h4" gutterBottom>
          Tournaments Management
        </Typography>
        <Button variant="contained" color="primary" onClick={handleOpenAdd}>
          Add New Tournament
        </Button>
      </Box>

      <TableContainer component={Paper} style={{ marginTop: 20 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Sport</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liveScoringStore.tournaments.map((tournament) => (
              <TableRow key={tournament.id}>
                <TableCell>{tournament.id}</TableCell>
                <TableCell>{tournament.name}</TableCell>
                <TableCell>{tournament.sport?.name || 'N/A'}</TableCell>
                <TableCell>{tournament.gender?.title || 'N/A'}</TableCell>
                <TableCell align="right">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handleOpenEdit(tournament)}
                    style={{ marginRight: 8 }}
                  >
                    Edit
                  </Button>
                  <IconButton
                    color="secondary"
                    onClick={() => handleOpenDeleteDialog(tournament)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TournamentForm
        open={isModalOpen}
        onClose={handleCloseModal}
        tournament={selectedTournament}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            {tournamentToDelete?.name || 'this tournament'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
});

export default TournamentsScreen;
